// Sharetribe line items from a VAT resolution. Spec §3. CommonJS: shared by
// server and client; decimal.js is a plain package, the SDK types come via ./sdk.
const Decimal = require('decimal.js');
const { types } = require('./sdk');
const { PATH } = require('./transaction');

const { Money } = types;

const LINE_ITEM = {
  NIGHT: 'line-item/night',
  SALE_TAX: 'line-item/sale-tax',
  PROVIDER_SALE_TAX: 'line-item/provider-sale-tax',
  CUSTOMER_COMMISSION: 'line-item/customer-commission',
  CUSTOMER_COMMISSION_VAT: 'line-item/customer-commission-vat',
  // Built by no path any more (customer service fee spec 2026-10-05); kept
  // so transactions made under the provider commission can still be read.
  PROVIDER_COMMISSION: 'line-item/provider-commission',
  PROVIDER_COMMISSION_VAT: 'line-item/provider-commission-vat',
};

// Freedomly's service fee as a percentage of the customer's price (VAT 0).
const DEFAULT_FEE_PERCENTAGE = 15;

const money = (amount, currency) => new Money(amount, currency);

// 0.255 → 25.5. Decimal parses the shortest decimal form, so no float noise.
const percentageOf = rate => new Decimal(rate).times(100).toNumber();

// Mirrors server/api-util/lineItemHelpers.calculateLineTotal: round half up
// to whole subunits, exactly as Sharetribe does when it validates the line.
const lineTotal = lineItem => {
  const { unitPrice, quantity, percentage } = lineItem;
  const amount = new Decimal(unitPrice.amount);
  let total;
  if (quantity) {
    total = amount.times(quantity);
  } else if (percentage) {
    total = amount.times(percentage).dividedBy(100);
  } else {
    throw new Error(`Line item ${lineItem.code} needs quantity or percentage`);
  }
  return money(total.toNearest(1, Decimal.ROUND_HALF_UP).toNumber(), unitPrice.currency);
};

const sumFor = (lineItems, party) =>
  lineItems
    .filter(li => li.includeFor.includes(party))
    .reduce((sum, li) => sum + lineTotal(li).amount, 0);

const lineTotals = lineItems => {
  const currency = lineItems[0].unitPrice.currency;
  return {
    payinTotal: money(sumFor(lineItems, 'customer'), currency),
    payoutTotal: money(sumFor(lineItems, 'provider'), currency),
  };
};

// A VAT line, or nothing when the rate is 0 (percentage: 0 is not a valid line).
const taxLine = (code, supply, unitPrice, includeFor) =>
  supply && supply.rate > 0
    ? [{ code, unitPrice, percentage: percentageOf(supply.rate), includeFor }]
    : [];

// Line items per path (spec §3). `customerPrice` is the customer's price
// excluding VAT (the offer, package or bid amount, N). The fee is
// `feePercentage` of it, billed to the customer with the VAT of the
// `serviceFee` supply; the work line is what is left (P = N − fee), so the
// two always sum to N to the cent.
const buildLineItems = ({
  path,
  resolution,
  customerPrice,
  feePercentage = DEFAULT_FEE_PERCENTAGE,
}) => {
  if (resolution && resolution.hold) {
    throw new Error('Cannot build line items while VAT validation is pending');
  }
  if (resolution && resolution.blocked) {
    throw new Error(`Cannot build line items for a blocked resolution: ${resolution.blocked}`);
  }
  if (!resolution || !resolution.supplies) {
    throw new Error('Cannot build line items without a VAT resolution');
  }
  // A resolution for another path has other supply keys; building from it
  // would silently drop the VAT lines.
  if (resolution.path && resolution.path !== path) {
    throw new Error(`VAT path mismatch: ${path} vs ${resolution.path}`);
  }
  const supplies = resolution.supplies;
  const hasFee =
    typeof feePercentage === 'number' && Number.isFinite(feePercentage) && feePercentage > 0;
  const fee = hasFee
    ? {
        code: LINE_ITEM.CUSTOMER_COMMISSION,
        unitPrice: customerPrice,
        percentage: feePercentage,
        includeFor: ['customer'],
      }
    : null;
  const feeAmount = fee ? lineTotal(fee).amount : 0;
  const providerPrice = money(customerPrice.amount - feeAmount, customerPrice.currency);
  const night = {
    code: LINE_ITEM.NIGHT,
    unitPrice: providerPrice,
    quantity: 1,
    includeFor: ['customer', 'provider'],
  };
  const feeLines = fee
    ? [
        fee,
        ...taxLine(LINE_ITEM.CUSTOMER_COMMISSION_VAT, supplies.serviceFee, lineTotal(fee), [
          'customer',
        ]),
      ]
    : [];

  if (path === PATH.STRIPE) {
    const saleTax = taxLine(LINE_ITEM.SALE_TAX, supplies.core, providerPrice, [
      'customer',
      'provider',
    ]);
    return [night, ...saleTax, ...feeLines];
  }
  if (path === PATH.INVOICE || path === PATH.LIGHT_MAIN) {
    // Freedomly is the principal: it sells the work and the fee to the
    // customer at its own rate, and buys the work from the provider at the
    // provider's rate (dynamic VAT decision 1, service fee decision 3).
    const saleTax = taxLine(LINE_ITEM.SALE_TAX, supplies.customerSale, providerPrice, ['customer']);
    const providerSaleTax = taxLine(
      LINE_ITEM.PROVIDER_SALE_TAX,
      supplies.providerSale,
      providerPrice,
      ['provider']
    );
    return [night, ...saleTax, ...feeLines, ...providerSaleTax];
  }
  if (path === PATH.LIGHT_CARD) {
    // The ghost payout transaction is the customer side of light-main; both
    // of its parties are Freedomly, so the VAT line is included for both.
    const saleTax = taxLine(LINE_ITEM.SALE_TAX, supplies.customerSale, providerPrice, [
      'customer',
      'provider',
    ]);
    return [night, ...saleTax, ...feeLines];
  }
  throw new Error(`Unknown VAT path: ${path}`);
};

module.exports = {
  LINE_ITEM,
  DEFAULT_FEE_PERCENTAGE,
  percentageOf,
  lineTotal,
  lineTotals,
  buildLineItems,
};
